import { Box, Grid, TextField, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { Icon } from "../../components/Icon/Icon.component";
import { FormValues } from "../../routes/Profile/AddSkillBook/AddSkillBook.types";
import DataPickerExternalTrainingCst from "../InputCst/DataPickerExternalTrainingCst";
import SelectCst from "../InputCst/SelectCst";


interface LogisticaSectionProps {
  expanded: boolean;
  onToggle: () => void;
  formValues: FormValues;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  errors: Record<string, string>;
  trainingTypeOptions: { label: string; value: string | number }[];
  t: (key: string) => string;
  handleDateChange: (name: string) => (date: Date | null) => void;
  readOnly?: boolean;
}

const LogisticaSection: React.FC<LogisticaSectionProps> = ({
  expanded,
  onToggle,
  formValues,
  setFormValues,
  errors,
  trainingTypeOptions,
  t,
  handleDateChange,
  readOnly
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev: FormValues) => ({ ...prev, [name]: value }));
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCustomSize = useMediaQuery("(max-width: 1350px)");


  return (
    <Box
      sx={ {
        background: theme.customColors.backgroundPrimary,
        borderRadius: "12px",
        mb: 2,
        border: `${theme.customColors.borderAccent} 1px solid`,
        p: 2,
        boxShadow: `${theme.customColors.borderAccent} 0 0 10px`,
        maxHeight: expanded ? undefined : "65px",
        gap: isMobile ? 1 : 0,
        flexDirection: isCustomSize ? "column" : "row"
      } }
    >
      <Box
        onClick={ onToggle }
        aria-pressed={ expanded }
        sx={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          mb: 2
        } }
      >
        <Typography sx={ { fontWeight: "bold", color: theme.customColors.textPrimary, fontSize: "1.2rem" } }>
          { t("logistica_section") }
        </Typography>
        <Icon
          icon="dropdown"
          color={ theme.customColors.systemPrimary02 }
          width="24px"
          height="19px"
          style={ {
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform .4s linear",
            color: theme.customColors.borderAccent
          } }
        />
      </Box>

      { expanded && (
        <Grid container spacing={ 2 }>
          <Grid item xs={ 12 }>
            <p style= { { fontSize:"10px" } }>{ t("site_field") }</p>
            <TextField
              fullWidth
              name="site"
              label="-"
              value={ formValues.location || "" }
              onChange={ handleInputChange }
              error={ !!errors.location }
              helperText={ errors.location }
              InputProps={ { style: { backgroundColor: theme.customColors.backgroundPrimary, color: theme.customColors.textPrimary } } }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
              disabled={ readOnly }
            />
          </Grid>
          <Box
            sx={ {
              display: "flex",
              alignItems: isCustomSize ? "stretch" : "flex-start",
              width: isMobile ? "100%" : "auto",
              cursor: "pointer",
              gap: 1 ,
              flexDirection: isCustomSize ? "column" : "row",
              marginLeft: 2
            } }
          >
            <Grid item xs={ 6 }>
              <p style= { { fontSize:"10px" } }>{ t("startDate_field") }</p>
              <DataPickerExternalTrainingCst
                id="startDate"
                label=""
                name="startDate"
                value={ formValues.startDate ? new Date(formValues.startDate as string) : null }
                onChange={ handleDateChange("startDate") }
                required={ true }
                error={ !!errors["startDate"] }
                errorMessage={ errors["startDate"] }
                disabled={ readOnly }
              />
            </Grid>
            <Grid item xs={ 6 }>
              <p style= { { fontSize:"10px" } }>{ t("endDate_field") }</p>
              <DataPickerExternalTrainingCst
                id="endDate"
                label=""
                name="endDate"
                value={ formValues.endDate ? new Date(formValues.endDate as string) : null }
                onChange={ handleDateChange("endDate") }
                required={ true }
                error={ !!errors["endDate"] }
                errorMessage={ errors["endDate"] }
                disabled={ readOnly }
              />
            </Grid>
          </Box>


          <Grid item xs={ 12 }>
            <p style= { { fontSize:"10px" } }>{ t("trainingType_field") }</p>
            <SelectCst
              options={ trainingTypeOptions }
              value={ (formValues.trainingType as { label: string; value: string })?.value ?? "" }
              onChange={ (e) => {
                const value = e.target.value;
                const label = trainingTypeOptions.find(opt => opt.value === value)?.label || "";
                setFormValues(prev => ({
                  ...prev,
                  trainingType: { label, value }
                }));
              } }
              name="trainingType"
              label="-"
              readOnly={ readOnly }
            />
          </Grid>

          <Grid item xs={ 12 }>
            <p style= { { fontSize:"10px" } }>{ t("description_field") }</p>
            <TextField
              fullWidth
              multiline
              name="description"
              label="-"
              value={ formValues.description || "" }
              onChange={ handleInputChange }
              error={ !!errors.description }
              helperText={ errors.description }
              InputProps={ { style: { backgroundColor: theme.customColors.backgroundPrimary, color: theme.customColors.textPrimary } } }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
              disabled={ readOnly }
            />
          </Grid>

          <Grid item xs={ 12 } sm={ 6 }>
            <p style= { { fontSize:"10px" } }>{ t("type_field") }</p>
            <TextField
              fullWidth
              multiline
              name="type"
              label="-"
              value={ formValues.type || "" }
              onChange={ handleInputChange }
              error={ !!errors.type }
              helperText={ errors.type }
              InputProps={ { style: { backgroundColor: theme.customColors.backgroundPrimary, color: theme.customColors.textPrimary } } }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
              disabled={ readOnly }
            />
          </Grid>

          <Grid item xs={ 12 } sm={ 6 }>
            <p style= { { fontSize:"10px" } }>{ t("away_trip_field") }</p>
            <TextField
              fullWidth
              multiline
              name="businessTrip"
              label="-"
              value={ formValues.businessTrip || "" }
              onChange={ handleInputChange }
              error={ !!errors.businessTrip }
              helperText={ errors.businessTrip }
              InputProps={ { style: { backgroundColor: theme.customColors.backgroundPrimary, color: theme.customColors.textPrimary } } }
              sx={ {
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: theme.customColors.textDisabledAlternative, // testo disabilitato
                  opacity: 1
                },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: theme.customColors.backgroundPrimary,
                  color: theme.customColors.textPrimary,
                  borderRadius: "8px",
                  "& fieldset": {
                    borderColor: theme.customColors.border
                  }
                },
                "& .MuiInputLabel-root": {
                  color: theme.customColors.textPrimary,
                  backgroundColor: theme.customColors.backgroundPrimary,
                  borderRadius: "8px",
                  paddingX: 0.5
                }
              } }
              disabled={ readOnly }
            />
          </Grid>
        </Grid>
      ) }
    </Box>
  );
};

export default LogisticaSection;
